import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArenaLogo } from "../../components/common/ArenaLogo";
import { brandAuthService } from "../../services/brand-auth.service";
import { AxiosError } from "axios";
import showPasswordIcon from "../../assets/show-password.svg";
import { useAuth } from "@/contexts/AuthContext";
import { authService } from "@/services/auth.service";
import { readReturnUrl, withReturnUrl } from "@/utils/returnUrl";
import { beginGoogleSignIn, GoogleSignInErrorState } from "@/lib/googleSignIn";
import { clearOnboardingSkip } from "@/lib/brandOnboarding";
import { usePortalBranding } from "@/contexts/portalBranding";

/**
 * The backend refuses valid credentials that have no relationship with the
 * brand portal they were used on (a.reveshare.com must not sign in
 * b.reveshare.com's accounts). That refusal names the brand; show it as-is.
 */
function portalAccessDeniedMessage(error: AxiosError<unknown>): string | null {
  const data = error.response?.data as
    | { code?: string; message?: string }
    | undefined;
  return error.response?.status === 403 &&
    data?.code === "PORTAL_ACCESS_DENIED" &&
    data.message
    ? data.message
    : null;
}

export const Login = () => {


  const navigate = useNavigate();
  const { brandAuth, login } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<"affiliate" | "brand">(
    "affiliate"
  );
  const location = useLocation();
  const [returnUrl, setReturnUrl] = useState("");
  const [isMobile, _] = useState(false);
  const portalBranding = usePortalBranding();

  // Google sign-in leaves the page: a redirect through the backend, which is
  // what lets it work from every white-label portal host (see lib/googleSignIn).
  // The outcome arrives on /affiliate/auth/google/complete.
  // Deliberately no loading state: if the user presses Back on Google's
  // screen, the browser may restore this page as-is, and a stuck spinner
  // would leave every button disabled.
  const handleGoogleLogin = () => {
    setError(null);
    beginGoogleSignIn({
      from: `${location.pathname}${location.search}`,
      returnUrl: returnUrl || undefined,
    });
  };

  // A failed Google sign-in comes back here with the reason in the state.
  useEffect(() => {
    const state = location.state as GoogleSignInErrorState | null;
    if (state?.googleError) {
      setError(state.googleError);
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
  }, [location, navigate]);

  // Extract parameters from query and handle Shopify auth flow
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const returnUrlParam = searchParams.get("returnUrl");
    const shopParam = searchParams.get("shop");
    const hostParam = searchParams.get("host");
    const redirectParam = searchParams.get("redirect");

    // Handle Shopify authentication redirect flow
    if (shopParam && hostParam) {
      const tokenParam = searchParams.get("token");

      if (tokenParam) {
        // Store the token for brand authentication
        localStorage.setItem("brand_token", tokenParam);
        // Fresh session: incomplete setups should land on onboarding again
        clearOnboardingSkip();
        // Force a storage event since it doesn't fire for same-tab changes
        window.dispatchEvent(new Event("storage"));

        if (redirectParam === "/brand_pricing") {
          // Backend set redirect=/brand_pricing, meaning user doesn't have subscription
          navigate("/brand/settings?tab=pricing", { replace: true });
          return;
        } else {
          // Backend didn't set redirect parameter, meaning user has active subscription
          navigate("/brand/dashboard", { replace: true });
          return;
        }
      }
    }

    if (returnUrlParam) {
      // readReturnUrl rejects off-origin values, so a crafted
      // `?returnUrl=https://evil.com` can't turn this page into a redirector.
      setReturnUrl(readReturnUrl(location.search) || "");
    }
  }, [location, navigate]);

  // useEffect(() => {
  //   window.addEventListener("resize", () => {
  //     setIsMobile(window.innerWidth < 450);
  //   });
  //   return () => {
  //     window.removeEventListener("resize", () => {
  //       setIsMobile(window.innerWidth < 450);
  //     });
  //   };
  // }, []);

  const handleAffiliateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    console.log("Starting login process...");

    try {
      // Always remember user for 30 days by default
      const response = await authService.signIn({
        email: formData.email,
        password: formData.password,
        rememberMe: true, // Always set to true for 30-day remembering
      });
      console.log("Login API response:", response);

      login(response);
      console.log("Auth context updated with user:", response.user);

      // If returnUrl exists, navigate there, otherwise go to dashboard
      if (returnUrl) {
        navigate(returnUrl);
      } else {
        navigate("/affiliate/dashboard");
      }
    } catch (err: any) {
      const error = err as AxiosError<{ message: any }>;
      console.error("Login error:", error);
      if (error.response?.status === 400) {
        setError("Invalid email or password");
      } else if (error.response?.status === 401) {
        setError("Unauthorized access. Please check your credentials.");
      } else if (error.response?.status === 403) {
        setError(
          portalAccessDeniedMessage(error) ??
            "Access forbidden. Please contact support."
        );
      } else if (error.code === "ERR_NETWORK") {
        setError("Network error. Please check your internet connection.");
      } else {
        setError("An error occurred. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBrandSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await brandAuthService.signIn({
        email: formData.email,
        password: formData.password,
        rememberMe: true, // Always remember user for 30 days
      });

      // Store user data and token in context
      brandAuth.login(response);

      // Back to the brand page that sent them to log in (e.g. the Klaviyo
      // install link), else the dashboard.
      const from = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
      const target =
        from?.pathname.startsWith("/brand/")
          ? `${from.pathname}${from.search ?? ""}`
          : returnUrl?.startsWith("/brand/")
            ? returnUrl
            : "/brand";

      // Add a small delay before navigation to ensure context is updated
      setTimeout(() => {
        navigate(target, { replace: true });
      }, 100);
    } catch (err) {
      if (err instanceof AxiosError) {
        // Handle Axios specific errors
        if (err.response?.status === 400) {
          setError("Invalid email or password");
        } else if (err.response?.status === 401) {
          setError("Unauthorized access. Please check your credentials.");
        } else if (err.response?.status === 403) {
          setError(
            portalAccessDeniedMessage(err) ??
              "Access forbidden. Please contact support."
          );
        } else if (err.code === "ERR_NETWORK") {
          setError("Network error. Please check your internet connection.");
        } else {
          setError("An error occurred. Please try again later.");
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderError = () =>
    error && (
      <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-lg">{error}</div>
    );

  return (
    <div className=" md:m-auto mx-auto w-[98%] md:w-max pt-4 md:pt-0 h-[88%] md:h-max">
      <div className="  h-full mx-auto flex items-center justify-center  md:px-5 md:py-7 py-6 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100] dark:shadow-none ">
        <div className="w-full overflow-y-auto h-full flex items-center  justify-center md:px-5 h-full">
          <div className="h-full  min-[340px]:px-5 min-[450px]:px-8 w-full sm:w-[380px] md:w-[380px]">
            <ArenaLogo className="w-12 border border-black mx-auto mb-4 rounded-[12px] bg-[#f6f6f6]" />
            <div className="mb-6 space-y-4">
              <h1 className="font-bold text-[24px] text-foreground/60 leading-[24px] tracking-[0.16px] text-center align-middle">
                Sign in
                {portalBranding.isWhiteLabel || portalBranding.isReady
                  ? ` to ${portalBranding.portalName}`
                  : ""}
              </h1>
            </div>
            <div className="mb-4 w-full flex justify-between items-center gap-2 ">
              <div
                className={` hover:cursor-pointer py-2 px-4 font-normal text-[14px] w-[45%] tracking-normal text-center align-middle text-foreground/60 border border-border rounded-[8px] ${
                  selectedRole === "affiliate"
                    ? "shadow-[0px_5px_30px_2px_rgb(var(--brand-600)/0.2),0px_2px_8px_0px_rgb(var(--brand-500)/0.2)] w-[55%] transition-all duration-300"
                    : ""
                }`}
                onClick={() => setSelectedRole("affiliate")}
              >
                I am an affiliate
              </div>
              <div
                className={` hover:cursor-pointer py-2 px-4 font-normal text-[14px] w-[45%] tracking-normal text-center align-middle text-foreground/60 border border-border rounded-[8px] ${
                  selectedRole === "brand"
                    ? "shadow-[0px_5px_30px_2px_rgb(var(--brand-600)/0.2),0px_2px_8px_0px_rgb(var(--brand-500)/0.2)] w-[55%] transition-all duration-300"
                    : ""
                }`}
                onClick={() => setSelectedRole("brand")}
              >
                I am a brand
              </div>
            </div>
            {selectedRole === "brand" && isMobile ? (
              <div>
                <a
                  href={
                    import.meta.env.VITE_SHOPIFY_APP_STORE_URL ||
                    "https://apps.shopify.com"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full mt-8 flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
                >
                  Install from Shopify App Store
                </a>
                <p className="mx-auto pt-4 font-light text-[14px] w-[330px] tracking-normal text-center align-middle text-muted-foreground">
                  Login on Desktop
                </p>
              </div>
            ) : (
              <div>
                {renderError()}

                <form
                  onSubmit={
                    selectedRole === "affiliate"
                      ? handleAffiliateSubmit
                      : handleBrandSubmit
                  }
                  className="space-y-4"
                >
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-[14px] font-medium text-muted-foreground"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="Enter your email"
                      className="mt-1 block w-full px-3 py-2 border border-border rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="block text-[14px] font-medium text-muted-foreground"
                    >
                      Password
                    </label>
                    <div className="relative mt-1">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        placeholder="Your password"
                        className="block w-full px-3 py-2 border border-border rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? (
                          <img src={showPasswordIcon} alt="show password" />
                        ) : (
                          <img src={showPasswordIcon} alt="show password" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Terms Agreement removed - only required at signup */}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-[8px] shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-600 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin mr-2" />
                    ) : null}
                    <span>Sign In</span>
                  </button>
                </form>

                {/* Google Sign In - Only for affiliates */}
                {selectedRole === "affiliate" && (
                  <>
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-card text-muted-foreground">
                          Or continue with
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleGoogleLogin()}
                      disabled={isLoading}
                      className="w-full py-2.5 px-4 bg-transparent border border-border rounded-lg text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Sign in with Google
                    </button>
                  </>
                )}

                <div className="mt-4 text-center h-[44px] flex items-center justify-center px-4 border border-brand-600 rounded-[8px]">
                  <span className="font-bold text-[14px] leading-[20px] tracking-normal text-muted-foreground">
                    Don't have an account?{" "}
                  </span>
                  <Link
                    to={
                      selectedRole === "affiliate"
                        ? // Someone who followed a join link and has no account
                          // yet must keep their destination through signup too.
                          withReturnUrl("/affiliate/auth/signup", returnUrl)
                        : "/brand/auth"
                    }
                    className="text-sm text-brand-600 font-bold text-[14px] tracking-normal hover:text-purple pl-1"
                  >
                    Sign up
                  </Link>
                </div>
                <div className="mt-[20px] text-center">
                  <Link
                    to={
                      selectedRole === "affiliate"
                        ? "/affiliate/auth/forgot-password"
                        : "/brand/auth/forgot-password"
                    }
                    className="text-sm text-brand-600 font-bold text-[14px] tracking-normal hover:text-purple pl-1"
                  >
                    Forgot password
                  </Link>
                </div>
                <div id="terms-of-service" className="mt-[20px] w-[194px] m-auto font-normal text-[12px] leading-[16px] tracking-normal text-muted-foreground text-center">
                  By signing in you agree to our <br />
                  <Link
                    to={"/terms"}
                    className="text-brand-600 text-[12px] tracking-normal hover:text-purple pl-1"
                  >
                    terms of service
                  </Link>{" "}
                  and{" "}
                  <Link
                    to={"/policy"}
                    className="text-brand-600  text-[12px] tracking-normal hover:text-purple pl-1"
                  >
                    privacy policy
                  </Link>
                  .
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
